import { Inject, Injectable } from "@angular/core";
import { Action, Store } from "@ngrx/store";
import { DocumentRef, isExternalUrl, isTrustedProtocolUrl, LocationService } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { isNil } from "lodash-es";

import { ROUTER_CONFIG, RouterConfig } from "../config/router-config.model";
import { RouterState } from "../state";
import { RouteAction } from "../store/route.action";

import { AnchorEventInfo } from "./link-handler.model";

/**
 * Service responsible for click processing of those links which belong to the application, but not maintained by application itself.
 * E.g. links coming from CRS in a way of html templates/documents,
 * which we display without any angular directives attached (e.g. obgRouteLink).
 * In general, clicking such links would reload the page. Which should be avoided.
 *
 * MICRO-FRONTEND NOTICE (#AC-1308):
 * This service can me marked for removal as potientially dangerous and unpredictable
 * in the environment with router per application strategy. Such strategy requires each application to handle its data independently
 * without subscribing to global document click events
 */
@Injectable({
	providedIn: "root",
})
export class LinkHandlerService {
	private logger: Log;

	constructor(
		private documentRef: DocumentRef,
		private store: Store<RouterState>,
		private locationService: LocationService,
		@Inject(ROUTER_CONFIG) private config: RouterConfig,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("linkHandler");
	}

	/** @internal */
	initialize() {
		this.documentRef.native.addEventListener("click", this.handler.bind(this), true);
	}

	dispose() {
		this.documentRef.native.removeEventListener("click", this.handler.bind(this));
	}

	private handler(e: MouseEvent) {
		const { shouldHandleEvent, href, isAnchorLink, isExternal, target, anchor } = this.getEventInfo(e);

		if (isNil(href) || isTrustedProtocolUrl(href, this.config.trustedDeeplinkProtocols)) {
			return undefined;
		}

		if (isExternal && anchor?.hasAttribute("obg-async-navigation")) {
			e.preventDefault();
			return setTimeout(() => {
				this.locationService.navigate(href, target);
			});
		}

		if (isExternal || this.isTargetNewWindow(target)) {
			e.preventDefault();
			return this.locationService.navigate(href, target);
		}

		let action: Action | undefined;
		if (shouldHandleEvent) {
			action = RouteAction.navigateByUrl(href);
		} else if (isAnchorLink) {
			action = RouteAction.setFragment({ fragment: href, replaceUrl: true });
			this.store.dispatch(action);
		}

		if (!action) {
			return undefined;
		}

		this.logger.debug("handler", "preventing default...");
		e.preventDefault();
		this.store.dispatch(action);
	}

	/**
	 * Gets the href and a "should handle" recommendation, given an Event.
	 *
	 * @param event The Event to inspect for target anchor and href.
	 */
	private getEventInfo(event: MouseEvent): AnchorEventInfo {
		const info: AnchorEventInfo = {
			shouldHandleEvent: false,
			isAnchorLink: false,
			isExternal: false,
		};

		const anchor = this.findClosestAnchor(event.target as Element);
		if (!anchor) {
			return info;
		}

		const href = anchor.getAttribute("href") || undefined;
		info.anchor = anchor;
		info.href = href;
		info.isExternal = !!href && isExternalUrl(href);
		info.target = anchor.getAttribute("target") || undefined;

		if (this.isTargetNewWindow(info.target) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
			return info;
		}

		const leftButtonClicked = event.which === 1;
		let isRelative = false;
		let isJumpingAnchorLink = false;

		if (href) {
			isJumpingAnchorLink = href.charAt(0) === "#";
			info.isAnchorLink = href.indexOf("#") !== -1;
			isRelative = !(isJumpingAnchorLink || /^[a-z]+:/i.test(href));
		}

		info.shouldHandleEvent =
			leftButtonClicked &&
			((!isJumpingAnchorLink && info.isAnchorLink) ||
				(isRelative &&
					!anchor.hasAttribute("obg-route-link") &&
					!anchor.hasAttribute("obg-disable-link-handler")));

		return info;
	}

	/**
	 * Finds the closest ancestor that's an anchor element.
	 *
	 * @param el The element to search upward from.
	 * @returns The link element that is the closest ancestor.
	 */
	private findClosestAnchor(el: Element): HTMLAnchorElement | undefined {
		while (el) {
			if (el.tagName === "A") {
				return el as HTMLAnchorElement;
			}

			el = el.parentNode as Element;
		}
		return undefined;
	}

	/**
	 * Gets a value indicating whether or not an anchor targets the current window.
	 *
	 * @param target Attribute "Target" of anchor element.
	 * @returns True if the target is this window; false otherwise.
	 */
	private isTargetNewWindow(target: string | undefined): boolean {
		return !!target && target !== "_self" && target !== "top";
	}
}
